import { cotizarConEnvia } from './enviaService';
import { cotizarConNoventa9Minutos } from './noventa9minutosService';
import { cotizarConInterRapidisimo } from './interrapidisimoService';
import { cotizarConDeprisa } from './deprisaService';
import { cotizarConCoordinadora } from './coordinadoraService';
import { CotizacionesState, CotizacionData } from '../../types/cotizaciones';
import { resolveShippingDestinationCityCode } from '../../utils/shippingDestinationCityCode';
import {
  enrichFormDataForQuote,
  assertDestinationCityForQuote
} from '../../utils/quoteFormDataUtils';

export const obtenerTodasLasCotizaciones = async (
  formData: any, 
  orderDetail: any,
  selectedTipoServicio: string
): Promise<CotizacionesState> => {
  try {
    const preparedFormData = enrichFormDataForQuote(formData, orderDetail)
    assertDestinationCityForQuote(orderDetail, preparedFormData)

    // Función para verificar si es ciudad metropolitana
    const esCiudadMetropolitana = (ciudad: string) => {
      if (!ciudad) return false;
      // Códigos de ciudades metropolitanas
      const codigosMetropolitanos = ['05001', '05088', '05266', '05360', '05631'];
      return codigosMetropolitanos.includes(ciudad);
    };

    const codigoCiudadDestino = resolveShippingDestinationCityCode(orderDetail, preparedFormData)
    const esAreaMetropolitana = esCiudadMetropolitana(codigoCiudadDestino)

    console.log('🏙️ Ciudad destino (código):', codigoCiudadDestino)
    console.log('🏙️ Es área metropolitana:', esAreaMetropolitana)

    const esContraentrega = preparedFormData.metodoPago === 'cod'
    if (esContraentrega) {
      console.log('🚫 No solicitando cotización de Deprisa (pago contraentrega)')
    }

    // Carriers vía /envia-com/quote: interRapidisimo, noventa9Minutos, deprisa, coordinadora
    const promesasCotizacion: Promise<unknown>[] = [
      cotizarConEnvia(preparedFormData, orderDetail, selectedTipoServicio),
      cotizarConInterRapidisimo(preparedFormData, orderDetail),
      ...(esContraentrega ? [] : [cotizarConDeprisa(preparedFormData, orderDetail)]),
      cotizarConCoordinadora(preparedFormData, orderDetail)
    ]

    let noventa9MinutosResponse: PromiseSettledResult<unknown> | null = null

    if (esAreaMetropolitana) {
      console.log('✅ Solicitando cotización de 99 Minutos (área metropolitana)')
      promesasCotizacion.splice(1, 0, cotizarConNoventa9Minutos(preparedFormData, orderDetail))
    } else {
      console.log('🚫 No solicitando cotización de 99 Minutos (fuera del área metropolitana)')
    }

    const responses = await Promise.allSettled(promesasCotizacion)

    const incluirDeprisa = !esContraentrega
    const enviaResponse = responses[0]
    if (esAreaMetropolitana) {
      noventa9MinutosResponse = responses[1]
    }
    const interIdx = esAreaMetropolitana ? 2 : 1
    const interRapidisimoResponse = responses[interIdx]
    const deprisaResponse = incluirDeprisa
      ? responses[interIdx + 1]
      : { status: 'fulfilled' as const, value: { success: false } }
    const coordinadoraResponse = responses[interIdx + (incluirDeprisa ? 2 : 1)]

    return procesarRespuestas(
      enviaResponse,
      noventa9MinutosResponse,
      interRapidisimoResponse,
      deprisaResponse,
      coordinadoraResponse
    )
  } catch (error) {
    console.error('Error detallado en obtenerTodasLasCotizaciones:', error);
    
    // Proporcionar información más específica del error
    if (error instanceof Error) {
      throw new Error(`Error al obtener cotizaciones: ${error.message}`);
    } else if (typeof error === 'string') {
      throw new Error(`Error al obtener cotizaciones: ${error}`);
    } else {
      throw new Error('Error al obtener cotizaciones: Error desconocido');
    }
  }
};

const procesarRespuestas = (
  enviaResponse: any,
  noventa9MinutosResponse: any,
  interRapidisimoResponse: any,
  deprisaResponse: any,
  coordinadoraResponse: any
): CotizacionesState => {
  const cotizaciones: CotizacionData[] = []
  let enviaData = null
  let noventa9MinutosData = null
  let interRapidisimoData = null
  let deprisaData = null
  let coordinadoraData = null
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let error = null // Reservado para manejo de errores futuro

  // Procesar respuesta de Envía
  if (enviaResponse.status === 'fulfilled' && enviaResponse.value.success) {
    enviaData = enviaResponse.value;
    const precioEnvia = Math.round(enviaResponse.value.data.valor_flete + 
                       enviaResponse.value.data.valor_costom + 
                       enviaResponse.value.data.valor_otros);
    
    cotizaciones.push({
      carrier: 'Envía',
      success: true,
      data: enviaResponse.value.data,
      precio: precioEnvia,
      diasEntrega: enviaResponse.value.data.dias_entrega
    });
    console.log('✅ Envía: Cotización exitosa');
  } else if (enviaResponse.status === 'fulfilled' && !enviaResponse.value.success) {
    console.log('⚠️ Envía: Sin cobertura -', enviaResponse.value.data?.respuesta || 'Sin información');
    // No agregar a cotizaciones pero tampoco es un error crítico
  } else if (enviaResponse.status === 'rejected') {
    console.error('❌ Error en cotización Envía:', enviaResponse.reason);
    // No establecer error aquí, solo log para debugging
  }

  // Procesar respuesta de 99 Minutos (solo si se solicitó)
  if (noventa9MinutosResponse && noventa9MinutosResponse.status === 'fulfilled' && noventa9MinutosResponse.value.success) {
    noventa9MinutosData = noventa9MinutosResponse.value;
    
    // Verificar si el quote tiene datos válidos (no es un error)
    if (noventa9MinutosResponse.value.quote && 
        noventa9MinutosResponse.value.quote.success && 
        noventa9MinutosResponse.value.quote.data?.data &&
        noventa9MinutosResponse.value.quote.data.meta !== 'error') {
      
      console.log('✅ Procesando cotizaciones de 99 Minutos (área metropolitana)');
      noventa9MinutosResponse.value.quote.data.data.forEach((service: any) => {
        // Filtrar cotizaciones con service "carga_do" y "mensajeria_do"
        if (service.service !== 'carga_do' && service.service !== 'mensajeria_do') {
          cotizaciones.push({
            carrier: 'Noventa9Minutos',
            success: true,
            data: service,
            precio: Math.round(service.totalPrice || service.price || 0),
            diasEntrega: service.deliveryDate?.dateDifference || service.dias_entrega || service.delivery_days
          });
        }
      });
    } else {
      console.log('🚫 99 Minutos: No hay cotizaciones disponibles o hay error en la respuesta:', noventa9MinutosResponse.value.quote?.data?.error);
    }
  } else if (noventa9MinutosResponse && noventa9MinutosResponse.status === 'rejected') {
    console.error('Error en cotización 99 Minutos:', noventa9MinutosResponse.reason);
    // No establecer error aquí, solo log para debugging
  } else if (!noventa9MinutosResponse) {
    console.log('🚫 99 Minutos: No se solicitó cotización (fuera del área metropolitana)');
  }

  // Procesar respuesta de Inter Rapidisimo
  if (interRapidisimoResponse.status === 'fulfilled' && interRapidisimoResponse.value.success) {
    interRapidisimoData = interRapidisimoResponse.value;
    
    // Verificar si el quote tiene datos válidos (no es un error)
    if (interRapidisimoResponse.value.quote && 
        interRapidisimoResponse.value.quote.success && 
        interRapidisimoResponse.value.quote.data?.data &&
        interRapidisimoResponse.value.quote.data.meta !== 'error') {
      
      console.log('✅ InterRapidisimo: Procesando cotizaciones exitosas');
      interRapidisimoResponse.value.quote.data.data.forEach((service: any) => {
        // Filtrar cotizaciones con service "carga_do" y "mensajeria_do"
        if (service.service !== 'carga_do' && service.service !== 'mensajeria_do') {
          cotizaciones.push({
            carrier: 'InterRapidisimo',
            success: true,
            data: service,
            precio: Math.round(service.totalPrice || service.price || 0),
            diasEntrega: service.deliveryDate?.dateDifference || service.dias_entrega || service.delivery_days
          });
          console.log(`✅ InterRapidisimo: Agregada cotización ${service.service} - $${Math.round(service.totalPrice)}`);
        }
      });
    } else {
      console.log('🚫 InterRapidisimo: No hay cotizaciones disponibles o hay error en la respuesta:', interRapidisimoResponse.value.quote?.data?.error);
    }
  } else if (interRapidisimoResponse.status === 'rejected') {
    console.error('❌ Error en cotización Inter Rapidisimo:', interRapidisimoResponse.reason);
    // No establecer error aquí, solo log para debugging
  }

  // Procesar respuesta de Deprisa
  if (deprisaResponse.status === 'fulfilled' && deprisaResponse.value.success) {
    deprisaData = deprisaResponse.value;
    
    // Verificar si el quote tiene datos válidos (no es un error)
    if (deprisaResponse.value.quote && 
        deprisaResponse.value.quote.success && 
        deprisaResponse.value.quote.data?.data &&
        deprisaResponse.value.quote.data.meta !== 'error') {
      
      console.log('✅ Deprisa: Procesando cotizaciones exitosas');
      deprisaResponse.value.quote.data.data.forEach((service: any) => {
        // Filtrar cotizaciones con service "carga_do" y "mensajeria_do"
        if (service.service !== 'carga_do' && service.service !== 'mensajeria_do') {
          cotizaciones.push({
            carrier: 'Deprisa',
            success: true,
            data: {
              ...service,
              serviceDescription: 'Deprisa Estandar'
            },
            precio: Math.round(service.totalPrice || service.price || 0),
            diasEntrega: service.deliveryDate?.dateDifference || service.dias_entrega || service.delivery_days
          });
          console.log(`✅ Deprisa: Agregada cotización ${service.service} - $${Math.round(service.totalPrice)}`);
        }
      });
    } else {
      console.log('🚫 Deprisa: No hay cotizaciones disponibles o hay error en la respuesta:', deprisaResponse.value.quote?.data?.error);
    }
  } else if (deprisaResponse.status === 'rejected') {
    console.error('❌ Error en cotización Deprisa:', deprisaResponse.reason);
    // No establecer error aquí, solo log para debugging
  }

  // Procesar respuesta de Coordinadora
  if (coordinadoraResponse.status === 'fulfilled' && coordinadoraResponse.value.success) {
    coordinadoraData = coordinadoraResponse.value;

    if (coordinadoraResponse.value.quote &&
        coordinadoraResponse.value.quote.success &&
        coordinadoraResponse.value.quote.data?.data &&
        coordinadoraResponse.value.quote.data.meta !== 'error') {

      console.log('✅ Coordinadora: Procesando cotizaciones exitosas');
      coordinadoraResponse.value.quote.data.data.forEach((service: any) => {
        if (service.service !== 'carga_do' && service.service !== 'mensajeria_do') {
          cotizaciones.push({
            carrier: 'Coordinadora',
            success: true,
            data: {
              ...service,
              serviceDescription: 'Coordinadora Ecommerce'
            },
            precio: Math.round(service.totalPrice || service.price || 0),
            diasEntrega: service.deliveryDate?.dateDifference || service.dias_entrega || service.delivery_days
          });
          console.log(`✅ Coordinadora: Agregada cotización ${service.service} - $${Math.round(service.totalPrice)}`);
        }
      });
    } else {
      console.log('🚫 Coordinadora: No hay cotizaciones disponibles o hay error en la respuesta:', coordinadoraResponse.value.quote?.data?.error);
    }
  } else if (coordinadoraResponse.status === 'rejected') {
    console.error('❌ Error en cotización Coordinadora:', coordinadoraResponse.reason);
  }

  // Ordenar por precio (más barato primero)
  cotizaciones.sort((a, b) => a.precio - b.precio);

  console.log(`📊 Total de cotizaciones encontradas: ${cotizaciones.length}`);
  console.log('📋 Cotizaciones disponibles:', cotizaciones.map(c => `${c.carrier}: $${c.precio}`));

  // Solo mostrar error si NO hay ninguna cotización exitosa
  // Esto significa que TODAS las transportadoras fallaron o no respondieron
  const finalError = cotizaciones.length === 0 ? 
    'No se pudieron obtener cotizaciones de ninguna transportadora disponible' : 
    null;

  // Log del resultado final
  if (cotizaciones.length > 0) {
    console.log('✅ Proceso exitoso: Se encontraron cotizaciones disponibles');
    console.log(`📋 Cotizaciones encontradas: ${cotizaciones.length}`);
    cotizaciones.forEach(cot => {
      console.log(`  - ${cot.carrier}: $${cot.precio} (${cot.diasEntrega} días)`);
    });
  } else {
    console.log('❌ Proceso fallido: Ninguna transportadora pudo cotizar');
    console.log('🔍 Revisando respuestas individuales...');
    
    // Log detallado de por qué falló cada transportadora
    if (enviaResponse.status === 'fulfilled' && !enviaResponse.value.success) {
      console.log(`  - Envía: ${enviaResponse.value.data?.respuesta || 'Sin información'}`);
    }
    if (noventa9MinutosResponse && noventa9MinutosResponse.status === 'fulfilled' && !noventa9MinutosResponse.value.success) {
      console.log(`  - 99 Minutos: ${noventa9MinutosResponse.value.message || 'Sin información'}`);
    }
    if (interRapidisimoResponse.status === 'fulfilled' && !interRapidisimoResponse.value.success) {
      console.log(`  - InterRapidisimo: ${interRapidisimoResponse.value.message || 'Sin información'}`);
    }
    if (deprisaResponse.status === 'fulfilled' && !deprisaResponse.value.success) {
      console.log(`  - Deprisa: ${deprisaResponse.value.message || 'Sin información'}`);
    }
    if (coordinadoraResponse.status === 'fulfilled' && !coordinadoraResponse.value.success) {
      console.log(`  - Coordinadora: ${coordinadoraResponse.value.message || 'Sin información'}`);
    }
  }

  return {
    envia: enviaData,
    noventa9Minutos: noventa9MinutosData,
    interRapidisimo: interRapidisimoData,
    deprisa: deprisaData,
    coordinadora: coordinadoraData,
    loading: false,
    error: finalError
  };
};
